import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Table,
  Tag,
  Space,
  Row,
  Col,
  Statistic,
  App,
  Dropdown,
  Select,
  DatePicker
} from 'antd';
import {
  PlusOutlined,
  AlertOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  MoreOutlined,
  EditOutlined,
  EyeOutlined,
  ReloadOutlined,
  FilterOutlined
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { trpc } from '../lib/api';
import { EmergencyWorkCreateModal } from '../components/emergency/EmergencyWorkCreateModal';
import { PageHeader, PageContainer } from '../components/common';

const { RangePicker } = DatePicker;

interface EmergencyWork {
  id: string;
  displayId: string;
  title: string;
  description?: string;
  priority: 'urgent' | 'high' | 'medium' | 'low';
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled' | 'postponed';
  impactLevel?: 'critical' | 'major' | 'minor';
  affectedAreas?: string;
  estimatedDowntime?: number;
  reportedAt?: Date;
  reportedBy?: string;
  scheduledDate: Date;
  actualStart?: Date;
  actualEnd?: Date;
  assignedTo?: string;
  equipmentId: string;
  emergencyNotes?: string;
}

type EmergencyWorkFilters = {
  status?: EmergencyWork['status'][];
  priority?: EmergencyWork['priority'][];
  impactLevel?: NonNullable<EmergencyWork['impactLevel']>[];
  dateRange?: { start: Date; end: Date };
};

const IMPACT_LEVEL_COLORS = {
  critical: 'red',
  major: 'orange',
  minor: 'yellow'
} as const;

const IMPACT_LEVEL_LABELS = {
  critical: '重大',
  major: '大',
  minor: '小'
} as const;

const STATUS_COLORS = {
  scheduled: 'blue',
  in_progress: 'orange',
  completed: 'green',
  cancelled: 'red',
  postponed: 'volcano'
} as const;

const STATUS_LABELS = {
  scheduled: '予定',
  in_progress: '実行中',
  completed: '完了',
  cancelled: 'キャンセル',
  postponed: '延期'
} as const;

const PRIORITY_COLORS = {
  urgent: 'red',
  high: 'orange',
  medium: 'blue',
  low: 'default'
} as const;

const PRIORITY_LABELS = {
  urgent: '緊急',
  high: '高',
  medium: '中',
  low: '低'
} as const;

export default function EmergencyWorkPage(): JSX.Element {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<EmergencyWorkFilters>({});

  // 緊急作業一覧取得
  const { data, isLoading, refetch } = trpc.schedule.listEmergency.useQuery({
    ...filters,
    page,
    limit: 20
  });

  const emergencyWorks = useMemo(() => data?.items ?? [], [data?.items]);
  const total = data?.total ?? 0;

  // 統計計算
  const stats = useMemo(() => {
    const reported = emergencyWorks.filter((w) => w.status === 'scheduled').length;
    const inProgress = emergencyWorks.filter((w) => w.status === 'in_progress').length;
    const completed = emergencyWorks.filter((w) => w.status === 'completed').length;
    const critical = emergencyWorks.filter((w) => w.impactLevel === 'critical').length;

    return {
      reported,
      inProgress,
      completed,
      critical
    };
  }, [emergencyWorks]);

  const handleDetail = (work: EmergencyWork) => {
    navigate(`/app/schedule/${work.id}`);
  };

  const handleEdit = (work: EmergencyWork) => {
    navigate(`/app/schedule/${work.id}`);
  };

  const getActionMenu = (record: EmergencyWork): MenuProps['items'] => {
    return [
      {
        key: 'detail',
        icon: <EyeOutlined />,
        label: '詳細表示',
        onClick: () => handleDetail(record)
      },
      {
        key: 'edit',
        icon: <EditOutlined />,
        label: '編集',
        onClick: () => handleEdit(record)
      }
    ];
  };

  const columns: ColumnsType<EmergencyWork> = [
    {
      title: 'タイトル',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      fixed: 'left',
      render: (text: string, record: EmergencyWork) => (
        <Button type="link" onClick={() => handleDetail(record)} style={{ padding: 0, fontWeight: 'bold' }}>
          {text}
        </Button>
      )
    },
    {
      title: '影響レベル',
      dataIndex: 'impactLevel',
      key: 'impactLevel',
      width: 100,
      render: (level?: 'critical' | 'major' | 'minor') =>
        level ? (
          <Tag color={IMPACT_LEVEL_COLORS[level]} icon={<WarningOutlined />}>
            {IMPACT_LEVEL_LABELS[level]}
          </Tag>
        ) : (
          '-'
        )
    },
    {
      title: '優先度',
      dataIndex: 'priority',
      key: 'priority',
      width: 80,
      render: (priority: 'urgent' | 'high' | 'medium' | 'low') => (
        <Tag color={PRIORITY_COLORS[priority]}>{PRIORITY_LABELS[priority]}</Tag>
      )
    },
    {
      title: 'ステータス',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: keyof typeof STATUS_LABELS) => (
        <Tag color={STATUS_COLORS[status]}>{STATUS_LABELS[status]}</Tag>
      )
    },
    {
      title: '報告日時',
      dataIndex: 'reportedAt',
      key: 'reportedAt',
      width: 160,
      render: (date?: Date) => (date ? dayjs(date).format('YYYY/MM/DD HH:mm') : '-'),
      sorter: (a, b) => {
        const aTime = a.reportedAt ? new Date(a.reportedAt).getTime() : 0;
        const bTime = b.reportedAt ? new Date(b.reportedAt).getTime() : 0;
        return bTime - aTime;
      },
      defaultSortOrder: 'ascend'
    },
    {
      title: '推定ダウンタイム',
      dataIndex: 'estimatedDowntime',
      key: 'estimatedDowntime',
      width: 140,
      render: (minutes?: number) => (minutes ? `${minutes}分` : '-')
    },
    {
      title: '影響範囲',
      dataIndex: 'affectedAreas',
      key: 'affectedAreas',
      ellipsis: true,
      render: (text?: string) => text ?? '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      fixed: 'right',
      render: (_: unknown, record: EmergencyWork) => (
        <Dropdown menu={{ items: getActionMenu(record) }} placement="bottomRight">
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      )
    }
  ];

  function handleFilterChange<K extends keyof EmergencyWorkFilters>(
    key: K,
    value: EmergencyWorkFilters[K]
  ) {
    setFilters((prev) => ({
      ...prev,
      [key]: value
    }));
    setPage(1);
  }

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      setFilters((prev) => ({
        ...prev,
        dateRange: {
          start: dates[0].toDate(),
          end: dates[1].toDate()
        }
      }));
    } else {
      setFilters((prev) => {
        const next = { ...prev };
        delete next.dateRange;
        return next;
      });
    }
    setPage(1);
  };

  const handleCreateSuccess = () => {
    setIsCreateModalOpen(false);
    message.success('緊急作業を作成しました');
    refetch();
  };

  return (
    <PageContainer>
      <PageHeader
        title="緊急作業"
        subtitle="緊急対応が必要な作業の報告と追跡を行います"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
              更新
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsCreateModalOpen(true)}
            >
              緊急作業を報告
            </Button>
          </Space>
        }
      />

      <Space direction="vertical" size="large" style={{ width: '100%' }}>

        {/* 統計カード */}
        <Row gutter={16}>
          <Col span={6}>
            <Card>
              <Statistic
                title="報告済み"
                value={stats.reported}
                prefix={<AlertOutlined style={{ color: '#1890ff' }} />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="対応中"
                value={stats.inProgress}
                prefix={<ClockCircleOutlined style={{ color: '#faad14' }} />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="完了"
                value={stats.completed}
                prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="重大な障害"
                value={stats.critical}
                prefix={<WarningOutlined style={{ color: '#ff4d4f' }} />}
              />
            </Card>
          </Col>
        </Row>

        {/* フィルタ */}
        <Card size="small">
          <Space wrap>
            <span>
              <FilterOutlined /> フィルタ:
            </span>
            <Select
              mode="multiple"
              placeholder="ステータス"
              style={{ minWidth: 200 }}
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value as EmergencyWorkFilters['status'])}
              options={[
                { label: '予定', value: 'scheduled' },
                { label: '実行中', value: 'in_progress' },
                { label: '完了', value: 'completed' },
                { label: 'キャンセル', value: 'cancelled' }
              ]}
            />
            <Select
              mode="multiple"
              placeholder="優先度"
              style={{ minWidth: 180 }}
              value={filters.priority}
              onChange={(value) => handleFilterChange('priority', value as EmergencyWorkFilters['priority'])}
              options={[
                { label: '緊急', value: 'urgent' },
                { label: '高', value: 'high' },
                { label: '中', value: 'medium' },
                { label: '低', value: 'low' }
              ]}
            />
            <Select
              mode="multiple"
              placeholder="影響レベル"
              style={{ minWidth: 180 }}
              value={filters.impactLevel}
              onChange={(value) => handleFilterChange('impactLevel', value as EmergencyWorkFilters['impactLevel'])}
              options={[
                { label: '重大', value: 'critical' },
                { label: '大', value: 'major' },
                { label: '小', value: 'minor' }
              ]}
            />
            <RangePicker
              placeholder={['報告開始日', '報告終了日']}
              onChange={handleDateRangeChange}
            />
          </Space>
        </Card>

        {/* テーブル */}
        <Card>
          <Table<EmergencyWork>
            columns={columns}
            dataSource={emergencyWorks}
            rowKey="id"
            loading={isLoading}
            pagination={{
              current: page,
              pageSize: 20,
              total,
              onChange: setPage,
              showSizeChanger: false,
              showTotal: (total) => `全 ${total} 件`
            }}
            scroll={{ x: 1200 }}
          />
        </Card>
      </Space>

      {/* 緊急作業作成モーダル */}
      <EmergencyWorkCreateModal
        open={isCreateModalOpen}
        onCancel={() => setIsCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
      />
    </PageContainer>
  );
}
